import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Hash, Lock, Plus, Send, Smile, Paperclip, Image as ImageIcon, MessageSquare,
  Pencil, Trash2, CornerUpLeft, X, Users, Search, ChevronLeft, ChevronRight, Briefcase,
  FileText, User as UserIcon, Camera, Share2, AtSign, Bell,
} from "lucide-react";
import { toast } from "sonner";
import { chatApi, profileApi, pushApi } from "../lib/api";
import { playPing } from "../lib/sound";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";
import ChatAvatar from "../components/chat/ChatAvatar";
import { NewChannelModal, NewDmModal, BrowseChannelsModal, AddMembersModal, ShareModal } from "../components/chat/ChatModals";
import { buildReplyTree } from "../lib/chatReplyTree";
import { useChatSearch } from "../components/chat/useChatSearch";
import { ChatSearchDropdown } from "../components/chat/ChatSearchDropdown";
import { useNavigationHistory } from "../contexts/NavigationHistoryContext";

const REACTIONS = ["👍", "❤️", "😂", "🎉", "🙏", "✅", "👀", "🔥"];
const EMOJIS = ["😀", "😁", "😂", "🤣", "😊", "😍", "😎", "🤔", "👍", "👏", "🙏", "🔥", "🎉", "❤️", "✅", "🚀", "💯", "👀", "😅", "🙌"];
const SHARE_ICON = { job: Briefcase, estimate: FileText, customer: UserIcon };

function timeOf(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}
function dayOf(iso) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const same = d.toDateString() === today.toDateString();
    return same ? "Today" : d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function Messages() {
  const { user, refreshMe } = useAuth();
  const { unread, refreshUnread, suppressNextNotify } = useChat();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const { goBack } = useNavigationHistory();
  const [searchParams, setSearchParams] = useSearchParams();

  const [members, setMembers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [convDetail, setConvDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [search, setSearch] = useState("");

  const [modal, setModal] = useState(null); // 'channel' | 'dm' | 'browse' | 'members' | 'share'
  const [replyTarget, setReplyTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const memberMap = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m])), [members]);
  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);
  const avatarInputRef = useRef(null);

  const me = useMemo(() => memberMap[user?.id] || { id: user?.id, name: user?.name, avatarUrl: user?.avatarUrl }, [memberMap, user]);

  // ----- initial load -----
  const loadConversations = useCallback(async () => {
    const cs = await chatApi.conversations();
    setConversations(cs);
    return cs;
  }, []);

  useEffect(() => {
    chatApi.members().then(setMembers).catch(() => setMembers([]));
    loadConversations().then((cs) => {
      const qid = searchParams.get("c");
      const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
      const target = cs.find((c) => c.id === qid) || (isDesktop ? cs[0] : null);
      if (target) setActiveId(target.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refresh conversation list + members presence periodically
  useEffect(() => {
    const id = setInterval(() => {
      loadConversations().catch(() => {});
      chatApi.members().then(setMembers).catch(() => {});
    }, 8000);
    return () => clearInterval(id);
  }, [loadConversations]);

  // ----- active conversation -----
  const loadMessages = useCallback(async (convId) => {
    setLoadingMsgs(true);
    try {
      const [det, msgs] = await Promise.all([
        chatApi.getConversation(convId),
        chatApi.messages(convId, { limit: 60 }),
      ]);
      setConvDetail(det);
      setMessages(msgs);
      lastTsRef.current = msgs.length ? msgs[msgs.length - 1].createdAt : null;
      await chatApi.markRead(convId);
      suppressNextNotify();
      refreshUnread();
    } finally {
      setLoadingMsgs(false);
    }
  }, [refreshUnread, suppressNextNotify]);

  useEffect(() => {
    if (!activeId) return;
    setMessages([]);
    setReplyTarget(null);
    setEditTarget(null);
    loadMessages(activeId);
    setSearchParams((p) => { const n = new URLSearchParams(p); n.set("c", activeId); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // "Currently active on this exact screen" signal for push suppression —
  // refreshed periodically while the channel stays open, cleared on leave.
  useEffect(() => {
    if (!activeId) return;
    const ping = () => pushApi.setActiveContext("conversation", activeId).catch(() => {});
    ping();
    const id = setInterval(ping, 30000);
    return () => { clearInterval(id); pushApi.clearActiveContext().catch(() => {}); };
  }, [activeId]);

  // Deep-link from a push notification (?m=<messageId>) — scroll to and
  // briefly highlight the message that triggered it, then clear the param.
  useEffect(() => {
    const mid = searchParams.get("m");
    if (!mid || messages.length === 0) return;
    const el = document.querySelector(`[data-testid="message-${mid}"]`);
    if (el) {
      el.scrollIntoView({ block: "center" });
      el.classList.add("ring-2", "ring-blue-400/60");
      setTimeout(() => el.classList.remove("ring-2", "ring-blue-400/60"), 2200);
    }
    setSearchParams((p) => { const n = new URLSearchParams(p); n.delete("m"); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Real-time: merge WS-delivered messages/edits/deletes instantly.
  useEffect(() => {
    if (!activeId) return;
    const onEvt = (e) => {
      const d = e.detail || {};
      if (d.conversationId !== activeId) return;
      if (d.type === "chat_message" && d.message) {
        if (d.message.parentId) return; // legacy thread-grouped messages are not surfaced in the flat feed
        setMessages((prev) => {
          if (prev.some((m) => m.id === d.message.id)) return prev;
          if (d.message.senderId !== user?.id) playPing();
          return [...prev, d.message];
        });
        lastTsRef.current = d.message.createdAt;
        chatApi.markRead(activeId).catch(() => {});
        suppressNextNotify();
        refreshUnread();
      } else if (d.type === "chat_message_updated" && d.message) {
        setMessages((prev) => prev.map((m) => m.id === d.message.id ? d.message : m));
      } else if (d.type === "chat_message_deleted" && d.messageId) {
        setMessages((prev) => prev.map((m) => m.id === d.messageId ? { ...m, deleted: true, text: "", attachments: [], sharedRef: null } : m));
      }
    };
    window.addEventListener("chat-event", onEvt);
    return () => window.removeEventListener("chat-event", onEvt);
  }, [activeId, refreshUnread, suppressNextNotify, user?.id]);

  // Also refresh the sidebar conversation list whenever any chat event fires
  // so lastMessagePreview + timestamp on rows stay live everywhere.
  useEffect(() => {
    const onEvt = () => { loadConversations(); };
    window.addEventListener("chat-event", onEvt);
    return () => window.removeEventListener("chat-event", onEvt);
  }, [loadConversations]);

  // Safety-net poll every 3s in case the WebSocket ever drops or the tenant
  // proxies WS. This can be lowered further once WS is battle-tested.
  useEffect(() => {
    if (!activeId) return;
    const id = setInterval(async () => {
      try {
        const after = lastTsRef.current;
        const fresh = await chatApi.messages(activeId, after ? { after } : { limit: 60 });
        if (fresh.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const add = fresh.filter((m) => !seen.has(m.id) && !m.parentId);
            if (add.some((m) => m.senderId !== user?.id)) playPing();
            // also merge updates (reactions/edits) for top-level already-seen
            const updated = prev.map((m) => fresh.find((f) => f.id === m.id) || m);
            return [...updated, ...add];
          });
          lastTsRef.current = fresh[fresh.length - 1].createdAt;
          await chatApi.markRead(activeId);
          suppressNextNotify();
          refreshUnread();
        } else {
          // periodically refresh reactions/edits of existing messages
          const full = await chatApi.messages(activeId, { limit: 60 });
          setMessages((prev) => prev.map((m) => full.find((f) => f.id === m.id) || m));
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, refreshUnread, suppressNextNotify]);

  // auto-scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeId]);

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      await profileApi.uploadAvatar(file);
      await refreshMe?.();
      chatApi.members().then(setMembers);
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const active = conversations.find((c) => c.id === activeId) || convDetail;

  const channels = conversations.filter((c) => c.type === "channel" || c.type === "group");
  const dms = conversations.filter((c) => c.type === "dm");
  const filterConv = (c) => !search || (c.name || "").toLowerCase().includes(search.toLowerCase());

  // Live search dropdown (channel names + message content within my conversations).
  const searchBoxRef = useRef(null);
  const { results: searchResults, open: searchOpen, setOpen: setSearchOpen, loading: searchLoading } = useChatSearch(search);
  useEffect(() => {
    const onClick = (e) => { if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [setSearchOpen]);
  const goToSearchResult = (r) => {
    setSearch("");
    setSearchOpen(false);
    if (r.conversationId === activeId) {
      // Already viewing this conversation — jump straight to the message.
      if (r.messageId) {
        const el = document.querySelector(`[data-testid="message-${r.messageId}"]`);
        if (el) {
          el.scrollIntoView({ block: "center" });
          el.classList.add("ring-2", "ring-blue-400/60");
          setTimeout(() => el.classList.remove("ring-2", "ring-blue-400/60"), 2200);
        }
      }
      return;
    }
    setActiveId(r.conversationId);
    setSearchParams((p) => {
      const n = new URLSearchParams(p);
      n.set("c", r.conversationId);
      if (r.messageId) n.set("m", r.messageId); else n.delete("m");
      return n;
    }, { replace: false });
  };

  const handleCreated = async (conv) => {
    setModal(null);
    await loadConversations();
    setActiveId(conv.id);
  };

  return (
    <div className="flex-1 min-h-0 flex bg-white" data-testid="messages-page">
      {/* Conversation list */}
      <aside className={`w-full md:w-72 shrink-0 border-r border-slate-200 flex flex-col bg-slate-50/60 ${activeId ? "hidden md:flex" : "flex"}`}>
        <div className="px-4 py-3.5 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 min-w-0">
            <button
              onClick={() => goBack("/mobile")}
              data-testid="messages-mobile-back"
              className="md:hidden h-8 w-8 -ml-1 shrink-0 rounded-md hover:bg-slate-200 flex items-center justify-center text-slate-300"
              aria-label="Back to dashboard"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h1 className="text-[16px] font-bold text-slate-900 flex items-center gap-2">
              Messages
              {unread.total > 0 && (
                <span className="relative inline-flex" data-testid="messages-alert-badge" title={`${unread.total} unread message${unread.total === 1 ? "" : "s"} waiting`}>
                  <Bell className="h-[18px] w-[18px] text-blue-400" />
                  <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {unread.total > 99 ? "99+" : unread.total}
                  </span>
                </span>
              )}
            </h1>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setModal("dm")} title="New direct message" data-testid="new-dm-btn" className="h-8 w-8 rounded-md hover:bg-slate-700/40 flex items-center justify-center text-white"><MessageSquare className="h-6 w-6 shrink-0" style={{color: '#ffffff'}} /></button>
              <button onClick={() => setModal("channel")} title="Create channel" data-testid="new-channel-btn" className="h-8 w-8 rounded-md hover:bg-slate-700/40 flex items-center justify-center text-white"><Plus className="h-6 w-6 shrink-0" style={{color: '#ffffff'}} /></button>
            </div>
          </div>
          <div className="relative mt-2.5" ref={searchBoxRef}>
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations" data-testid="conv-search" className="w-full h-8 pl-8 pr-2 rounded-md border border-slate-200 bg-white text-[12.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
            {searchOpen && (
              <ChatSearchDropdown
                results={searchResults}
                loading={searchLoading}
                query={search}
                onSelect={goToSearchResult}
              />
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
          <Section title="Channels" action={<button onClick={() => setModal("browse")} data-testid="browse-channels-btn" className="text-[11px] font-semibold text-blue-600 hover:underline">Browse</button>}>
            {channels.filter(filterConv).map((c) => (
              <ConvRow key={c.id} conv={c} active={c.id === activeId} unread={unread.byConversation?.[c.id] || 0} onClick={() => setActiveId(c.id)} />
            ))}
            {channels.length === 0 && <Empty text="No channels yet" />}
          </Section>
          <Section title="Direct Messages">
            {dms.filter(filterConv).map((c) => (
              <ConvRow key={c.id} conv={c} active={c.id === activeId} unread={unread.byConversation?.[c.id] || 0} onClick={() => setActiveId(c.id)} dm />
            ))}
            {dms.length === 0 && <Empty text="No direct messages" />}
          </Section>
        </div>
        {/* You + avatar upload */}
        <div className="border-t border-slate-200 p-3 flex items-center gap-2.5">
          <button onClick={() => avatarInputRef.current?.click()} className="relative group" data-testid="my-avatar-upload" title="Change profile photo">
            <ChatAvatar name={me.name} avatarUrl={me.avatarUrl} presence="online" size="md" />
            <span className="absolute inset-0 rounded-lg bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Camera className="h-3.5 w-3.5 text-white" /></span>
          </button>
          <div className="min-w-0">
            <div className="text-[12.5px] font-semibold text-slate-800 truncate">{me.name}</div>
            <div className="text-[11px] text-emerald-600">Active</div>
          </div>
          <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={onPickAvatar} />
        </div>
      </aside>

      {/* Message pane */}
      <main
        data-testid="messages-chat-pane"
        className={`min-w-0 flex flex-col bg-[#0A0F1C] transition-transform duration-300 ease-out fixed inset-0 z-40 md:static md:z-auto md:flex-1 md:translate-x-0 ${
          activeId ? "translate-x-0" : "translate-x-full pointer-events-none md:pointer-events-auto"
        }`}
      >
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-[13px]">Select a conversation to start chatting</div>
        ) : (
          <>
            <ConvHeader
              conv={active}
              detail={convDetail}
              onBack={() => setActiveId(null)}
              onAddMembers={() => setModal("members")}
              canManage={active.type !== "dm"}
            />
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-0.5 bg-[#e3e4e6]" data-testid="message-list">
              {loadingMsgs && messages.length === 0 && <div className="text-center text-slate-400 text-[12px] py-8">Loading messages…</div>}
              {!loadingMsgs && messages.length === 0 && <div className="text-center text-slate-400 text-[12px] py-8">No messages yet — say hello 👋</div>}
              <MessageStream
                messages={messages}
                memberMap={memberMap}
                meId={user?.id}
                isGroup={active?.type !== "dm"}
                onReact={async (m, emoji) => { const u = await chatApi.react(m.id, emoji); setMessages((prev) => prev.map((x) => (x.id === u.id ? u : x))); }}
                onDelete={async (m) => { await chatApi.remove(m.id); setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, deleted: true, text: "" } : x))); }}
                onReply={(m) => { setEditTarget(null); setReplyTarget(m); }}
                onEditRequest={(m) => { setReplyTarget(null); setEditTarget(m); }}
              />
            </div>
            <Composer
              key={activeId}
              members={members}
              meId={user?.id}
              onSend={async (payload) => {
                const msg = await chatApi.send(activeId, payload);
                setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
                lastTsRef.current = msg.createdAt;
                loadConversations();
                suppressNextNotify();
              }}
              onShare={() => setModal("share")}
              replyTarget={replyTarget ? { id: replyTarget.id, name: memberMap[replyTarget.senderId]?.name || "Unknown", text: replyTarget.deleted ? "This message was deleted" : (replyTarget.text || (replyTarget.attachments?.length ? "Attachment" : "")) } : null}
              onClearReply={() => setReplyTarget(null)}
              editTarget={editTarget}
              onClearEdit={() => setEditTarget(null)}
              onSubmitEdit={async (id, text) => {
                const u = await chatApi.edit(id, text);
                setMessages((prev) => prev.map((x) => (x.id === u.id ? u : x)));
                setEditTarget(null);
              }}
            />
          </>
        )}
      </main>

      {/* Modals */}
      {modal === "channel" && <NewChannelModal members={members} currentUserId={user?.id} onClose={() => setModal(null)} onCreated={handleCreated} />}
      {modal === "dm" && <NewDmModal members={members} currentUserId={user?.id} onClose={() => setModal(null)} onStarted={handleCreated} />}
      {modal === "browse" && <BrowseChannelsModal onClose={() => setModal(null)} onJoined={async (id) => { setModal(null); await loadConversations(); setActiveId(id); }} />}
      {modal === "members" && convDetail && (
        <AddMembersModal
          members={members}
          existingIds={convDetail.members || []}
          onClose={() => setModal(null)}
          onAdded={async (ids) => { await chatApi.addMembers(activeId, ids); toast.success("Added"); setModal(null); loadMessages(activeId); }}
        />
      )}
      {modal === "share" && (
        <ShareModal
          onClose={() => setModal(null)}
          onShare={async (ref) => {
            setModal(null);
            const msg = await chatApi.send(activeId, { sharedRef: ref });
            setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
            lastTsRef.current = msg.createdAt;
          }}
        />
      )}
    </div>
  );
}

/* ---------- Sidebar bits ---------- */

function Section({ title, action, children }) {
  return (
    <div>
      <div className="flex items-center justify-between px-2 mb-1">
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">{title}</div>
        {action}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="px-2 py-1.5 text-[11.5px] text-slate-400 italic">{text}</div>;
}

function ConvRow({ conv, active, unread, onClick, dm }) {
  const Icon = conv.type === "group" ? Lock : Hash;
  const preview = conv.lastMessage?.text || "No messages yet";
  const time = conv.lastMessageAt ? timeOf(conv.lastMessageAt) : "";
  return (
    <button onClick={onClick} data-testid={`conv-row-${conv.id}`} className="block w-full text-left">
      {/* Desktop — compact sidebar row */}
      <div
        className={`hidden md:flex items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors ${active ? "bg-blue-600 text-white" : "hover:bg-slate-200/60 text-slate-700"}`}
      >
        {dm ? (
          <ChatAvatar name={conv.name} avatarUrl={conv.avatarUrl} presence={conv.presence} size="sm" />
        ) : (
          <span className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${active ? "bg-white/20" : "bg-slate-200"}`}><Icon className={`h-3.5 w-3.5 ${active ? "text-white" : "text-slate-500"}`} /></span>
        )}
        <span className={`flex-1 min-w-0 truncate text-[13px] ${unread ? "font-bold" : "font-medium"}`}>{conv.name || "Direct message"}</span>
        {unread > 0 && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white text-blue-600" : "bg-rose-500 text-white"}`} data-testid={`conv-unread-${conv.id}`}>{unread}</span>
        )}
      </div>

      {/* Mobile — rich glowing card */}
      <div
        className="flex md:hidden items-center gap-3 p-3 mb-2.5 rounded-2xl active:scale-[0.99] transition-transform"
        style={{
          background: "linear-gradient(180deg,#0F1E3C 0%,#0B1730 100%)",
          border: "1.5px solid #389ECF",
          boxShadow: "0 0 24px -6px rgba(56,158,207,0.55), 0 10px 26px -12px rgba(0,0,0,0.8)",
        }}
      >
        {dm ? (
          <ChatAvatar name={conv.name} avatarUrl={conv.avatarUrl} presence={conv.presence} size="lg" />
        ) : (
          <span className="h-11 w-11 rounded-full flex items-center justify-center shrink-0 bg-blue-500/15 border border-blue-400/30">
            <Icon className="h-5 w-5 text-blue-300" strokeWidth={2.5} />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`truncate text-[15px] text-slate-100 ${unread ? "font-extrabold" : "font-bold"}`}>
              {conv.name || "Direct message"}
            </span>
            {time && <span className="shrink-0 text-[11px] text-slate-400">{time}</span>}
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <span className="truncate text-[12.5px] text-slate-400">{preview}</span>
            <span className="shrink-0 flex items-center gap-1.5">
              {unread > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-md bg-rose-500 text-white flex items-center justify-center" data-testid={`conv-unread-mobile-${conv.id}`}>
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
              <ChevronRight className="h-4 w-4 text-slate-500" />
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

/* ---------- Header ---------- */

function ConvHeader({ conv, detail, onBack, onAddMembers, canManage }) {
  const Icon = conv.type === "group" ? Lock : Hash;
  return (
    <div className="h-14 shrink-0 border-b border-slate-200 px-4 md:px-6 flex items-center justify-between">
      <div className="flex items-center gap-2.5 min-w-0">
        <button onClick={onBack} data-testid="messages-back-to-list" className="md:hidden h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"><ChevronLeft className="h-5 w-5 text-slate-600" /></button>
        {conv.type === "dm" ? (
          <ChatAvatar name={conv.name} avatarUrl={conv.avatarUrl} presence={conv.presence} size="md" />
        ) : (
          <span className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center"><Icon className="h-4 w-4 text-slate-500" /></span>
        )}
        <div className="min-w-0">
          <div className="text-[14.5px] font-bold text-slate-900 truncate flex items-center gap-1.5">{conv.name || "Direct message"}</div>
          <div className="text-[11.5px] text-slate-400 truncate">
            {conv.type === "dm" ? (conv.presence === "online" ? "Active now" : conv.presence || "") : `${conv.memberCount || (detail?.members?.length ?? 0)} members${conv.description ? " · " + conv.description : ""}`}
          </div>
        </div>
      </div>
      {canManage && (
        <button onClick={onAddMembers} data-testid="add-members-btn" className="flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-[12px] font-semibold text-slate-600">
          <Users className="h-3.5 w-3.5" /> Add
        </button>
      )}
    </div>
  );
}

/* ---------- Message stream ---------- */

function MessageStream({ messages, memberMap, meId, isGroup, onReact, onDelete, onReply, onEditRequest }) {
  let lastDay = null;
  const ordered = buildReplyTree(messages);
  return (
    <>
      {ordered.map(({ msg: m, isReply }) => {
        const day = dayOf(m.createdAt);
        const showDay = day !== lastDay;
        lastDay = day;
        // User picked: repeat avatar+name on every single message (no grouping).
        return (
          <React.Fragment key={m.id}>
            {showDay && (
              <div className="flex items-center gap-3 my-3" data-testid="day-divider">
                <div className="flex-1 h-px bg-slate-300" />
                <span className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide">{day}</span>
                <div className="flex-1 h-px bg-slate-300" />
              </div>
            )}
            <div className={isReply ? "ml-9 md:ml-11 pl-3 border-l-2 border-slate-300/70" : ""} data-testid={isReply ? `reply-row-${m.id}` : undefined}>
              <MessageRow
                m={m}
                sender={memberMap[m.senderId]}
                memberMap={memberMap}
                meId={meId}
                isGroup={isGroup}
                onReact={onReact}
                onDelete={onDelete}
                onReply={onReply}
                onEditRequest={onEditRequest}
              />
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
}

function ReactionBar({ reactions, meId, onReact, m }) {
  const grouped = {};
  (reactions || []).forEach((r) => { grouped[r.emoji] = grouped[r.emoji] || []; grouped[r.emoji].push(r.userId); });
  const keys = Object.keys(grouped);
  if (!keys.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {keys.map((e) => {
        const mine = grouped[e].includes(meId);
        return (
          <button
            key={e}
            onClick={() => onReact(m, e)}
            data-testid={`reaction-${m.id}-${e}`}
            className={`flex items-center gap-1 h-6 px-1.5 rounded-full border text-[11px] transition-colors ${mine ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 hover:border-slate-300"}`}
          >
            <span>{e}</span><span className="font-semibold text-black" style={{color: '#000000'}}>{grouped[e].length}</span>
          </button>
        );
      })}
    </div>
  );
}

function SharedCard({ ref: r }) {
  const Icon = SHARE_ICON[r.kind] || FileText;
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(r.url)}
      data-testid="shared-card"
      className="mt-1.5 flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50/40 text-left transition-colors max-w-sm"
    >
      <div className="h-8 w-8 rounded-md bg-white border border-slate-200 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-blue-600" /></div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{r.kind}</div>
        <div className="text-[13px] font-semibold text-slate-800 truncate">{r.label}</div>
        {r.sub && <div className="text-[11px] text-slate-400 truncate">{r.sub}</div>}
      </div>
    </button>
  );
}

function Attachment({ a }) {
  if (a.kind === "image") {
    return (
      <a href={chatApi.fileUrl(a.url)} target="_blank" rel="noreferrer" data-testid="attachment-image" className="block mt-1.5 w-fit">
        <img src={chatApi.fileUrl(a.url)} alt={a.filename} className="max-h-60 max-w-sm rounded-lg border border-slate-200 object-cover" />
      </a>
    );
  }
  return (
    <a href={chatApi.fileUrl(a.url)} target="_blank" rel="noreferrer" data-testid="attachment-file" className="mt-1.5 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:border-blue-300 text-[12.5px] text-slate-700">
      <Paperclip className="h-3.5 w-3.5 text-slate-400" /> <span className="truncate max-w-[200px]">{a.filename}</span>
    </a>
  );
}

function MessageRow({ m, sender, memberMap, meId, isGroup, onReact, onDelete, onReply, onEditRequest }) {
  const mine = m.senderId === meId;
  const [hover, setHover] = useState(false);
  const [pickReact, setPickReact] = useState(false);

  const name = sender?.name || "Unknown";
  const replyToName = m.replyTo ? (memberMap?.[m.replyTo.senderId]?.name || "Unknown") : null;

  return (
    <div
      className="group relative flex gap-3 mt-3 px-2 py-1 rounded-md hover:bg-black/5"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPickReact(false); }}
      data-testid={`message-${m.id}`}
    >
      {/* 40x40 rounded-md avatar — Slack-style */}
      <div className="shrink-0">
        <ChatAvatar
          name={name}
          avatarUrl={sender?.avatarUrl}
          presence={sender?.presence}
          size="lg"
          showPresence={false}
        />
      </div>

      <div className="flex-1 min-w-0">
        {/* Name + timestamp header */}
        <div className="flex items-baseline gap-2">
          <span className="text-[15px] font-bold text-[#1D1C1D]">{name}</span>
          <span className="text-[11px] text-[#616061]">{timeOf(m.createdAt)}</span>
        </div>

        {/* Inline reply quote — vertical line + original sender/text */}
        {m.replyTo && !m.deleted && (
          <div className="mt-1 mb-1 pl-2 border-l-2 border-blue-300 flex items-baseline gap-1.5 max-w-md" data-testid={`reply-quote-${m.id}`}>
            <span className="text-[12px] font-semibold text-blue-600 shrink-0">{replyToName}</span>
            <span className="text-[12px] text-slate-500 truncate">{m.replyTo.deleted ? "This message was deleted" : (m.replyTo.text || "Attachment")}</span>
          </div>
        )}

        {/* Message body — no bubble, black text */}
        {m.deleted ? (
          <div className="text-[13.5px] italic text-[#8a8b90] mt-0.5">This message was deleted</div>
        ) : (
          <>
            {m.text && <MessageText text={m.text} mine={mine} />}
            {(m.attachments || []).map((a) => <Attachment key={a.id} a={a} />)}
            {m.sharedRef && <SharedCard ref={m.sharedRef} />}
          </>
        )}

        {!m.deleted && m.editedAt && (
          <div className="text-[11px] text-[#8a8b90] mt-0.5" data-testid={`edited-label-${m.id}`}>(edited)</div>
        )}

        {!m.deleted && <ReactionBar reactions={m.reactions} meId={meId} onReact={onReact} m={m} />}
      </div>

      {/* Hover actions — larger clear-background icons, distinct colors per action */}
      {hover && !m.deleted && (
        <div className="absolute -top-4 right-2 flex items-center gap-1.5 border border-[#e2e8f0] rounded-lg shadow-md px-1.5 py-1 z-10" data-testid={`msg-actions-${m.id}`}>
          <div className="relative">
            <button onClick={() => setPickReact((v) => !v)} title="React" data-testid={`react-btn-${m.id}`} className="h-8 w-8 flex items-center justify-center text-gray-900"><Smile className="h-6 w-6 shrink-0" style={{color: '#000000'}} /></button>
            {pickReact && (
              <div className="absolute right-0 top-10 z-10 flex gap-0.5 bg-[#ffffff] border border-[#e2e8f0] rounded-lg shadow-lg p-1">
                {REACTIONS.map((e) => (
                  <button key={e} onClick={() => { onReact(m, e); setPickReact(false); }} data-testid={`react-pick-${m.id}-${e}`} className="h-8 w-8 rounded hover:bg-slate-100 text-[16px]">{e}</button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => onReply(m)} title="Reply" data-testid={`reply-btn-${m.id}`} className="h-8 w-8 flex items-center justify-center text-gray-900"><CornerUpLeft className="h-6 w-6 shrink-0" style={{color: '#000000'}} /></button>
          {mine && <button onClick={() => onEditRequest(m)} title="Edit" data-testid={`edit-btn-${m.id}`} className="h-8 w-8 flex items-center justify-center text-gray-900"><Pencil className="h-6 w-6 shrink-0" style={{color: '#000000'}} /></button>}
          {mine && <button onClick={() => onDelete(m)} title="Delete" data-testid={`delete-btn-${m.id}`} className="h-8 w-8 flex items-center justify-center text-gray-900"><Trash2 className="h-6 w-6 shrink-0" style={{color: '#000000'}} /></button>}
        </div>
      )}
    </div>
  );
}

function MessageText({ text, mine }) {
  // Highlight @mentions visually.
  const parts = text.split(/(@[\w.']+)/g);
  const mentionCls = "text-blue-600 font-semibold hover:underline cursor-pointer";
  return (
    <div className="text-[15px] leading-[1.45] whitespace-pre-wrap break-words text-[#1D1C1D]">
      {parts.map((p, i) =>
        p.startsWith("@") ? <span key={i} className={mentionCls}>{p}</span> : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </div>
  );
}

/* ---------- Composer ---------- */

function Composer({ members, meId, onSend, onShare, replyTarget, onClearReply, editTarget, onClearEdit, onSubmitEdit }) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [mention, setMention] = useState(null); // {query, ids}
  const [mentionIds, setMentionIds] = useState([]);
  const fileRef = useRef(null);
  const imgRef = useRef(null);
  const taRef = useRef(null);

  useEffect(() => {
    if (editTarget) {
      setText(editTarget.text || "");
      taRef.current?.focus();
    }
  }, [editTarget]);

  const onChange = (e) => {
    const val = e.target.value;
    setText(val);
    const upto = val.slice(0, e.target.selectionStart);
    const mtch = upto.match(/@([\w.']*)$/);
    setMention(mtch ? { query: mtch[1] } : null);
  };

  const mentionList = mention
    ? members.filter((m) => m.id !== meId && (m.name || "").toLowerCase().includes(mention.query.toLowerCase())).slice(0, 6)
    : [];

  const pickMention = (m) => {
    setText((t) => t.replace(/@([\w.']*)$/, `@${m.name.replace(/\s+/g, "")} `));
    setMentionIds((ids) => [...new Set([...ids, m.id])]);
    setMention(null);
    taRef.current?.focus();
  };

  const stageFiles = async (files, kindHint) => {
    setUploading(true);
    try {
      for (const f of files) {
        const a = await chatApi.uploadAttachment(f);
        setAttachments((prev) => [...prev, a]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      void kindHint;
    }
  };

  const send = async () => {
    const body = text.trim();
    if (editTarget) {
      if (!body) return;
      try {
        await onSubmitEdit(editTarget.id, body);
        setText("");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not save edit");
      }
      return;
    }
    if (!body && !attachments.length) return;
    const usedMentions = mentionIds.filter((id) => {
      const m = members.find((x) => x.id === id);
      return m && body.includes(`@${m.name.replace(/\s+/g, "")}`);
    });
    try {
      await onSend({ text: body, attachments, mentions: usedMentions, replyToId: replyTarget?.id || null });
      setText("");
      setAttachments([]);
      setMentionIds([]);
      onClearReply?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    }
  };

  return (
    <div className="shrink-0 border-t border-slate-200 px-4 md:px-6 py-3">
      {editTarget && (
        <div className="flex items-center justify-between mb-2 pl-3 pr-2 py-1.5 rounded-md bg-amber-50 border border-amber-200" data-testid="edit-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Pencil className="h-3.5 w-3.5 text-amber-600 shrink-0" />
            <span className="text-[12.5px] font-semibold text-amber-700">Editing message</span>
          </div>
          <button onClick={() => { onClearEdit?.(); setText(""); }} data-testid="edit-cancel-btn" className="h-6 w-6 rounded hover:bg-amber-100 flex items-center justify-center"><X className="h-3.5 w-3.5 text-amber-700" /></button>
        </div>
      )}
      {!editTarget && replyTarget && (
        <div className="flex items-center justify-between mb-2 pl-3 pr-2 py-1.5 rounded-md bg-blue-50 border border-blue-200" data-testid="reply-banner">
          <div className="flex items-center gap-2 min-w-0">
            <CornerUpLeft className="h-3.5 w-3.5 text-blue-600 shrink-0" />
            <span className="text-[12.5px] font-semibold text-blue-700 shrink-0">Replying to {replyTarget.name}</span>
            <span className="text-[12px] text-slate-500 truncate">{replyTarget.text}</span>
          </div>
          <button onClick={onClearReply} data-testid="reply-cancel-btn" className="h-6 w-6 rounded hover:bg-blue-100 flex items-center justify-center"><X className="h-3.5 w-3.5 text-blue-700" /></button>
        </div>
      )}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map((a, i) => (
            <div key={a.id} className="flex items-center gap-1.5 h-7 pl-2 pr-1 rounded-md bg-slate-100 text-[11.5px] text-slate-600">
              <Paperclip className="h-3 w-3" /> <span className="truncate max-w-[120px]">{a.filename}</span>
              <button onClick={() => setAttachments((p) => p.filter((_, idx) => idx !== i))} className="h-5 w-5 rounded hover:bg-slate-200 flex items-center justify-center"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="relative flex items-end gap-1 rounded-xl border border-slate-200 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400">
        {/* mention dropdown */}
        {mentionList.length > 0 && (
          <div className="absolute bottom-full left-2 mb-2 w-60 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-20" data-testid="mention-dropdown">
            {mentionList.map((m) => (
              <button key={m.id} onClick={() => pickMention(m)} data-testid={`mention-option-${m.id}`} className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50 text-left">
                <ChatAvatar name={m.name} avatarUrl={m.avatarUrl} presence={m.presence} size="sm" />
                <span className="text-[12.5px] font-medium text-slate-800 truncate">{m.name}</span>
              </button>
            ))}
          </div>
        )}
        {/* emoji popover */}
        {showEmoji && (
          <div className="absolute bottom-full left-2 mb-2 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-2 grid grid-cols-8 gap-0.5 z-20" data-testid="emoji-popover">
            {EMOJIS.map((e) => (
              <button key={e} onClick={() => { setText((t) => t + e); setShowEmoji(false); taRef.current?.focus(); }} className="h-8 w-8 rounded hover:bg-slate-100 text-[17px]">{e}</button>
            ))}
          </div>
        )}
        {/* Left toolbar: image, attachment, emoji, share — 36px square, single row */}
        <div className="flex items-center gap-1 pb-0.5">
          <button onClick={() => imgRef.current?.click()} disabled={!!editTarget} title="Upload image" data-testid="composer-image" className="h-9 w-9 rounded-md bg-sky-500 hover:bg-sky-600 shadow-sm flex items-center justify-center transition-colors disabled:opacity-40"><ImageIcon className="h-[18px] w-[18px] shrink-0" strokeWidth={2.5} color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} /></button>
          <button onClick={() => fileRef.current?.click()} disabled={!!editTarget} title="Attach file" data-testid="composer-file" className="h-9 w-9 rounded-md bg-emerald-500 hover:bg-emerald-600 shadow-sm flex items-center justify-center transition-colors disabled:opacity-40"><Paperclip className="h-[18px] w-[18px] shrink-0" strokeWidth={2.5} color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} /></button>
          <button onClick={() => setShowEmoji((v) => !v)} title="Emoji" data-testid="composer-emoji" className="h-9 w-9 rounded-md bg-amber-500 hover:bg-amber-600 shadow-sm flex items-center justify-center transition-colors"><Smile className="h-[18px] w-[18px] shrink-0" strokeWidth={2.5} color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} /></button>
          {onShare && <button onClick={onShare} disabled={!!editTarget} title="Share a Job / Estimate / Customer" data-testid="composer-share" className="h-9 w-9 rounded-md bg-violet-500 hover:bg-violet-600 shadow-sm flex items-center justify-center transition-colors disabled:opacity-40"><Share2 className="h-[18px] w-[18px] shrink-0" strokeWidth={2.5} color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} /></button>}
        </div>
        <textarea
          ref={taRef}
          value={text}
          onChange={onChange}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } if (e.key === "Escape" && editTarget) { onClearEdit?.(); setText(""); } }}
          rows={1}
          placeholder={editTarget ? "Edit message…" : replyTarget ? "Reply…" : "Message…"}
          data-testid="composer-input"
          className="flex-1 resize-none bg-transparent px-1 py-1.5 text-[13.5px] focus:outline-none max-h-32 min-h-[36px]"
        />
        <div className="flex items-center pb-0.5">
          <button onClick={send} disabled={uploading || (!text.trim() && !attachments.length)} data-testid="composer-send" className="h-9 px-3 gap-1.5 rounded-md bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-40 text-[12.5px] font-semibold"><Send className="h-[15px] w-[15px]" /><span className="hidden sm:inline">{editTarget ? "Save" : "Send"}</span></button>
        </div>
        <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) stageFiles(f, "image"); }} />
        <input ref={fileRef} type="file" accept="application/pdf,.doc,.docx,.csv,.txt,image/*" hidden onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) stageFiles(f, "file"); }} />
      </div>
      {uploading && <div className="text-[11px] text-slate-400 mt-1">Uploading…</div>}
    </div>
  );
}
